import { ArrowRight, ImagePlus } from "lucide-react";
import garageBefore from "@/assets/garage-clear-out-before.webp.asset.json";
import garageAfter from "@/assets/garage-clear-out-after.webp.asset.json";
import propertyBefore from "@/assets/property-clear-out-before.webp.asset.json";
import propertyAfter from "@/assets/property-clear-out-after.webp.asset.json";
import householdBefore from "@/assets/household-rubbish-before.webp.asset.json";
import householdAfter from "@/assets/household-rubbish-after.webp.asset.json";
import moveOutBefore from "@/assets/move-out-clear-out-before.webp.asset.json";
import moveOutAfter from "@/assets/move-out-clear-out-after.webp.asset.json";

/**
 * Before & after showcase built for real OZ WASTE job photography.
 * Drop a real image URL into `before` / `after` for a category and the
 * placeholder frame is replaced automatically, no other change needed.
 * Intentionally ships with no imagery so nothing on the page implies
 * results we haven't actually delivered.
 */
type Job = { title: string; before?: string; after?: string };

const jobs: Job[] = [
  { title: "Garage Clear-Out", before: garageBefore.url, after: garageAfter.url },
  { title: "Move-Out Clear-Out", before: moveOutBefore.url, after: moveOutAfter.url },
  { title: "Household Rubbish", before: householdBefore.url, after: householdAfter.url },
  { title: "Property Clear-Out", before: propertyBefore.url, after: propertyAfter.url },
];

const Frame = ({ src, label, alt }: { src?: string; label: string; alt: string }) => (
  <div className="relative flex-1 aspect-[4/3] rounded-lg overflow-hidden bg-[#1a1a1a]/[0.04] border border-[#1a1a1a]/10">
    {src ? (
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        width={1000}
        height={750}
        className="w-full h-full object-cover"
      />
    ) : (
      <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-[#1a1a1a]/35">
        <ImagePlus className="w-6 h-6" />
        <span className="text-xs font-semibold">Photo coming soon</span>
      </div>
    )}
    <span className="absolute top-2 left-2 rounded-full bg-[#1a1a1a] text-[#FDC00E] text-[10px] font-black uppercase tracking-wider px-2.5 py-1">
      {label}
    </span>
  </div>
);

const BeforeAfter = () => (
  <section className="bg-[#f7f7f7] py-16 md:py-24">
    <div className="container">
      <h2 className="text-3xl md:text-4xl font-black text-[#1a1a1a] text-center mb-3 leading-tight">
        FROM THIS <span className="text-orange">→</span> TO THIS
      </h2>
      <p className="text-center text-[#1a1a1a]/70 mb-12 md:mb-14 max-w-xl mx-auto">
        We clear the clutter so you get your space back.
      </p>

      <div className="grid sm:grid-cols-2 gap-6">
        {jobs.map((job) => (
          <div
            key={job.title}
            className="bg-white rounded-xl border border-[#1a1a1a]/8 shadow-[0_2px_12px_rgba(0,0,0,0.04)] p-5"
          >
            <div className="flex items-center gap-3">
              <Frame src={job.before} label="Before" alt={`${job.title} before`} />
              <ArrowRight className="w-5 h-5 shrink-0 text-orange" />
              <Frame src={job.after} label="After" alt={`${job.title} after`} />
            </div>
            <h3 className="mt-4 text-base font-bold text-[#1a1a1a]">{job.title}</h3>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default BeforeAfter;
